import { Link } from "react-router-dom";
import { ApiError, shortfallOf } from "../api/client";
import { TopUp } from "./Credits";
import {
  type ErrorContext,
  type Params,
  messageFor,
  sentenceFor,
} from "../copy/errors";
import { formatDuration } from "./format";

const LINE = "m-0 text-sm text-slate-700";

/** A mutation's failure as a sentence.
 *
 *  Without a `context`:
 *  `insufficient_credits` is the block of §15.1 (`ui/Credits.tsx::TopUp`):
 *  what the action needs, what is available (`detail {needed, available,
 *  top_up_url}`) and **Top up** — with a context too;
 *  `validation_error` is shown verbatim
 *  because its message names the JSON path (or the form field) the user must
 *  fix.
 *
 *  With a `context` (every screen of Objects) the sentence is
 *  `sentenceFor(error, context)`: the server's message is never shown.
 *
 *  Either way `storage_full` ends with the link to the files page. */
export function ErrorLine({
  error,
  context,
}: {
  error: unknown;
  context?: ErrorContext;
}) {
  if (!(error instanceof ApiError)) {
    return (
      <p role="alert" className={LINE}>
        {messageFor(undefined)}
      </p>
    );
  }
  const short = shortfallOf(error);
  if (short) {
    // one block everywhere (§15.1, P28-T08): what it needs, what is
    // available, and Top up
    return <TopUp shortfall={short} />;
  }
  if (error.code === "storage_full") {
    return (
      <p role="alert" className={LINE}>
        {context ? sentenceFor(error, context) : messageFor(error.code)}{" "}
        <Link to="/media">Free up space in Media.</Link>
      </p>
    );
  }
  if (context) {
    return (
      <p role="alert" className={LINE}>
        {sentenceFor(error, context)}
      </p>
    );
  }
  if (error.code === "validation_error") {
    return (
      <p role="alert" className={LINE}>
        {error.message}
      </p>
    );
  }
  if (error.code === "restitch_limit") {
    const detail = (error.detail ?? {}) as {
      retry_after?: number;
      max?: number;
    };
    return (
      <p role="alert" className={LINE}>
        {messageFor(error.code)}
        {typeof detail.max === "number"
          ? ` Up to ${detail.max} re-stitches a day per job;`
          : ""}
        {typeof detail.retry_after === "number"
          ? ` try again in about ${formatDuration(detail.retry_after)}.`
          : ""}
      </p>
    );
  }
  return (
    <p role="alert" className={LINE}>
      {messageFor(
        error.code,
        error.detail && typeof error.detail === "object"
          ? (error.detail as Params)
          : null,
      )}
    </p>
  );
}
